// Interceptor de mock — solo activo si environment.useMocks === true
// Intercepta URLs de la API y devuelve datos mock con delay simulado
import { HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { of, delay } from 'rxjs';
import { API_ENDPOINTS } from '../app/core/config/api.config';
import {
  MOCK_VEHICLE_BRANDS,
  MOCK_CITIES,
  MOCK_VEHICLE_YEARS,
  MOCK_VEHICLES_BY_PLATE,
} from './mock-data/vehicles.mock';
import { MOCK_PERSONS_BY_PLATE } from './mock-data/persons.mock';
import { MOCK_LEAD_PLANS } from './mock-data/lead-plans.mock';
import { MOCK_QUOTE_RESPONSE } from './mock-data/quote-response.mock';
import { ApiResponse } from '../app/core/models/api-response.model';

// Delay aleatorio entre 500ms y 1500ms para simular latencia real
const randomDelay = (): number => Math.floor(Math.random() * 1000) + 500;

// Crea una respuesta mock exitosa
function mockResponse<T>(data: T): HttpResponse<ApiResponse<T>> {
  return new HttpResponse({
    status: 200,
    body: {
      data,
      message: 'OK',
      statusCode: 200,
      success: true,
    },
  });
}

// Mapa de URLs mock → datos de respuesta
function getMockResponse(url: string): HttpResponse<unknown> | null {
  // Auth token
  if (url.includes('/auth/token') || url.includes('/auth/refresh')) {
    return mockResponse({
      accessToken: 'mock-token-' + Date.now(),
      sessionId: 'mock-session-' + Date.now(),
      expiresIn: 3600,
      issuedAt: Date.now(),
    });
  }

  // Modelos por marca
  if (url.includes('/api/v1/movilidad/vehiculo/modelos')) {
    const parsedUrl = new URL(url, 'http://localhost');
    const brandCode = (parsedUrl.searchParams.get('marca') ?? '').toUpperCase();
    const brand = MOCK_VEHICLE_BRANDS.find((item) => (item.brandSol ?? '').toUpperCase() === brandCode);
    return mockResponse(
      (brand?.models ?? []).map((model) => ({
        codigoSolMarca: brand?.brandSol,
        descripcionMarca: brand?.brand ?? brand?.name,
        codigoModeloSol: model.id,
        descripcionModelo: model.name,
      })),
    );
  }

  // Modelos por marca (legacy endpoint)
  if (url.includes('/vehicles/brands/') && url.includes('/models')) {
    const brandId = url.split('/vehicles/brands/')[1]?.split('/models')[0] ?? '';
    const brand = MOCK_VEHICLE_BRANDS.find((item) => item.id === brandId);
    return mockResponse(brand?.models ?? []);
  }

  // Marcas de vehículos
  if (url.includes('/vehicles/brands')) {
    return mockResponse(MOCK_VEHICLE_BRANDS);
  }

  // Años de vehículos
  if (url.includes('/vehicles/years')) {
    return mockResponse(MOCK_VEHICLE_YEARS);
  }

  // Búsqueda de vehículo por placa
  if (url.includes('/vehicles/lookup/')) {
    const plate = url.split('/vehicles/lookup/')[1]?.toUpperCase() ?? '';
    return mockResponse(MOCK_VEHICLES_BY_PLATE[plate] ?? null);
  }

  // Búsqueda de persona por placa del vehículo
  if (url.includes('/persons/lookup/')) {
    const plate = url.split('/persons/lookup/')[1]?.toUpperCase() ?? '';
    return mockResponse(MOCK_PERSONS_BY_PLATE[plate] ?? null);
  }

  // Ciudades
  if (url.includes(API_ENDPOINTS.cities.list)) {
    return mockResponse(MOCK_CITIES);
  }

  // Lead: planes del lead (debe evaluarse antes de update-lead)
  if (/\/lead\/[^/]+\/plans/.exec(url)) {
    return new HttpResponse({
      status: 200,
      body: {
        data: MOCK_LEAD_PLANS,
        meta: { request_id: 'abc-123-def-459', timestamp: new Date().toISOString() },
        message: 'OK',
        statusCode: 200,
        success: true,
      },
    });
  }

  // Lead: update
  if (url.includes('/update-lead/')) {
    return mockResponse({ success: true });
  }

  // Cotización
  if (url.includes(API_ENDPOINTS.quoter.calculate)) {
    return mockResponse(MOCK_QUOTE_RESPONSE);
  }

  return null;
}

export const mockApiInterceptor: HttpInterceptorFn = (req, next) => {
  const mockResp = getMockResponse(req.url);

  if (mockResp) {
    return of(mockResp).pipe(delay(randomDelay()));
  }

  // Si no hay mock para esta URL, pasar al siguiente interceptor
  return next(req);
};
